import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AttachmentLinkSharedBase, AttachmentSharedBase, AttachmentUploadUrl} from '../attachments.interface';
import {UserProfileService} from '@app/shared/services/api/user-profile.service';
import {Observable, Subject, Subscription, finalize, takeUntil, tap} from 'rxjs';
import {ToasterService} from '../../toaster/toaster.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AceLoggerService} from '@app/logging/ace-logger.service';
import {AceLogger} from '@app/logging/ace-logger';
import {ngfDrop, ngfSelect} from 'angular-file';
import {ToastType} from '../../toaster/Toast';
import {BrowserUtils} from '@app/shared/util/general/browser-utils';
import {saveAs} from 'file-saver';
import {FileNamesModalComponent} from '../file-names-modal/file-names-modal.component';
import {AttachmentService, AttachmentServiceToken} from '../attachment.service';
import moment from 'moment-timezone';
import {ConfirmationComponent} from '@shared/components/confirmation/confirmation.component';
import {IAlbum, LIGHTBOX_EVENT, Lightbox, LightboxEvent} from 'ngx-lightbox';
import {UploadStateComponent} from '../upload-state/upload-state.component';
import {ArrayUtils} from '@app/shared/util/collections/collection-utils';

export enum AttachmentDeleteAccess {
  ALLOWED = 'allowed',
  ALLOWED_OWN = 'allowed_own',
  NOT_ALLOWED = 'not_allowed'
}

export enum AttachmentPreviewAction {
  CLOSE = 1,
  OPEN,
  CHANGE
}

export interface AttachmentPreviewUserAction {
  action: AttachmentPreviewAction;
  attachmentId?: string;
  attachmentName?: string;
}

type lightboxEventTypes = typeof LIGHTBOX_EVENT[keyof typeof LIGHTBOX_EVENT];

interface LightboxEventObject {
  id: lightboxEventTypes,
  data?: number | null
}

type AttachmentClickAction = 'download' | 'lightbox';

interface AttachmentClickData {
  attachmentId: string;
  action: AttachmentClickAction;
}

interface LightboxData {
  subscription: Subscription;
  timeoutHandle: any;
  index: number;
  attachment_id: string;
  album: IAlbum[];
}


@Component({
  selector: 'app-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss'],
  standalone: false
})
export class AttachmentsComponent<RecordAttachment extends AttachmentSharedBase<AttachmentLinkSharedBase>, RecordId, UploadRequestBody> implements OnInit, OnChanges, OnDestroy {
  @Input() attachments: RecordAttachment[];
  @Input() recordId: RecordId;
  @Input() columnLayout = false;
  @Input() showPasteImageMsg = true;
  @Input() autoAssociate = true;
  @Input() disabled = false;
  @Input() attachmentIdToOpen: string;
  @Input() addAllowed = false;
  @Input() deleteAccess: AttachmentDeleteAccess = AttachmentDeleteAccess.NOT_ALLOWED;
  @Output() attachmentUploading = new EventEmitter<boolean>();
  @Output() attachmentsUpdateTrigger = new EventEmitter<void>();
  @Output() attachmentAssociated = new EventEmitter<RecordAttachment[]>();
  @Output() attachmentsFetched = new EventEmitter<RecordAttachment[]>();
  @Output() attachmentDeleted = new EventEmitter<RecordAttachment>();
  @Output() copyLink = new EventEmitter<RecordAttachment>();
  @Output() attachmentOpened = new EventEmitter<RecordAttachment>();
  @Output() attachmentPreviewUserAction = new EventEmitter<AttachmentPreviewUserAction>();
  @ViewChild(ngfDrop, {static: false}) dropDirective: ngfDrop;
  @ViewChild('fileSelect', {read: ngfSelect, static: false}) selectDirective: ngfSelect;
  @ViewChild(UploadStateComponent, {static: true}) uploadStateComponent: UploadStateComponent<RecordAttachment, RecordId, UploadRequestBody>;

  attachmentsWithPreview: RecordAttachment[];
  attachmentsWithoutPreview: RecordAttachment[];
  dropValid: any;
  pasteImage = {show: false, focused: false};
  attachmentsFetching = false;
  attachmentClickData: AttachmentClickData = null;

  readonly PREVIEW_DISPLAY_HEIGHT = 60;
  readonly PREVIEW_DISPLAY_WIDTH = 100;

  private toOpenAttachmentOnFetch = false;
  private ignoreLightboxCloseEvent: boolean;
  private album: IAlbum[];
  private lightboxData: LightboxData = null;
  private lastProcessedAttachments: RecordAttachment[];
  private readonly logger: AceLogger;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly linkExpiryThresholdMargin = moment.duration(2, 'minute');
  private readonly attachmentUserActionByLightboxEvent = new Map<lightboxEventTypes, AttachmentPreviewAction>([
    [LIGHTBOX_EVENT.CLOSE, AttachmentPreviewAction.CLOSE],
    [LIGHTBOX_EVENT.OPEN, AttachmentPreviewAction.OPEN],
    [LIGHTBOX_EVENT.CHANGE_PAGE, AttachmentPreviewAction.CHANGE],
  ]);

  constructor(@Inject(AttachmentServiceToken) private attachmentService: AttachmentService<RecordAttachment, RecordId, AttachmentUploadUrl, UploadRequestBody>,
              private modalService: NgbModal,
              private toasterService: ToasterService,
              private userProfileService: UserProfileService,
              private lightbox: Lightbox,
              private lightboxEvent: LightboxEvent,
              loggerService: AceLoggerService) {
    this.logger = loggerService.create(AttachmentsComponent.name);
  }

  ngOnInit() {
    const isMobile = BrowserUtils.isMobile();
    this.pasteImage.show = !isMobile;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.attachmentIdToOpen) {
      if (this.attachmentIdToOpen) {
        if (changes.attachments || !this.attachments) {
          this.toOpenAttachmentOnFetch = true;
        } else if ((this.lightboxData?.attachment_id ?? null) !== (this.attachmentIdToOpen ?? null)) {
          this.openAttachmentById(this.attachmentIdToOpen);
        }
      } else if (this.lightboxData) {
        this.closeLightbox();
      }
    }
    if (changes.attachments) {
      if (this.recordId != null) {
        if (this.attachments) {
          this.onAttachmentsUpdated(this.attachments);
        } else {
          this.refetchAttachments().pipe(takeUntil(this.ngUnsubscribe)).subscribe(attachments => {
            this.attachments = attachments;
            this.onAttachmentsUpdated(attachments);
          });
        }
      }
    }
    if (changes.deleteAccess && this.deleteAccess == null) {
      this.deleteAccess = AttachmentDeleteAccess.NOT_ALLOWED;
    }
  }

  openWithLightbox(attachment: RecordAttachment, index: number) {
    this.handleAttachmentClick(attachment, index, 'lightbox');
  }

  onAttachmentUploading(uploading: boolean) {
    this.attachmentUploading.emit(uploading);
  }

  onAttachmentAssociated(associated: RecordAttachment[]) {
    this.attachmentAssociated.emit(associated);
  }

  openUrl(attachment: RecordAttachment) {
    this.handleAttachmentClick(attachment, null, 'download');
  }

  onCopyLink(attachment: RecordAttachment): void {
    this.copyLink.emit(attachment);
  }

  canDeleteAttachment(attachment: RecordAttachment): boolean {
    const user = this.userProfileService.getLoggedInUser();
    return this.deleteAccess === AttachmentDeleteAccess.ALLOWED
      || (this.deleteAccess === AttachmentDeleteAccess.ALLOWED_OWN && user != null && attachment?.author_id === user.userId);
  }

  onDeleteAttachment(attachment: RecordAttachment) {
    ConfirmationComponent.createModal(this.modalService, {
      title: {messageKey: 'Delete Attachment'},
      body: {messageKey: 'Are you sure you want to delete filename?', parameters: {filename: attachment.file_name}},
      confirmFn: () => this.attachmentService.deleteAttachment(this.recordId, this.getAttachmentId(attachment)),
      afterConfirmFn: () => this.attachmentDeleted.emit(attachment)
    });
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.disabled) {
      const items = event?.clipboardData?.items;
      if (items?.length) {
        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item?.type?.indexOf('image') === 0) {
            files.push(item.getAsFile());
          }
        }

        if (files?.length) {
          const modalRef = this.modalService.open(FileNamesModalComponent, {centered: true });
          const fileNamesComponent: FileNamesModalComponent = modalRef.componentInstance;
          fileNamesComponent.fileNames = files.map(file => file.name);
          fileNamesComponent.existingFileNames = [...this.attachmentsWithPreview, ...this.attachmentsWithoutPreview].map(a => a.file_name);
          modalRef.result.then((fileNames: string[]) => {
            const filesToUpload: File[] = fileNames.map((fileName, i) => new File([files[i]], fileName, {type: files[i].type }));
            this.onFilesChange(filesToUpload);
          }, () => {
          });
        } else {
          this.toasterService.push({message: {messageKey: 'Could not find an image to paste' }, type: ToastType.Info, dismissible: true });
        }
      } else {
        this.toasterService.push({message: {messageKey: 'Could not find anything to paste' }, type: ToastType.Info, dismissible: true });
      }
    }
  }

  setPasteImageFocused(focused: boolean): void {
    this.pasteImage.focused = focused;
  }

  onFilesChange(updatedFiles: File[]): void {
    // Want to ensure that if more files are selected while upload is in progress, only the new files are provided
    this.resetFileSelectors();
    this.uploadStateComponent.uploadAttachments(updatedFiles, true);
  }
  
  openAttachmentById(attachmentId: string): void {
    const previewAttachmentIndex = this.findIndexForPreviewAttachments(attachmentId);
    if (previewAttachmentIndex >= 0) {
      this.handleAttachmentClick(this.attachmentsWithPreview[previewAttachmentIndex], previewAttachmentIndex, 'lightbox');
    } else {
      const noPreviewAttachmentIndex = this.findIndexForAttachments(this.attachmentsWithoutPreview, attachmentId);
      if (noPreviewAttachmentIndex >= 0) {
        this.handleAttachmentClick(this.attachmentsWithoutPreview[noPreviewAttachmentIndex], noPreviewAttachmentIndex, 'download');
      } else {
        this.toasterService.push({message: {messageKey: 'Attachment not found'}, type: ToastType.Danger, dismissible: true, timeout: -1});
      }
    }
  }

  associateUploadedFiles(recordId: RecordId): Observable<RecordAttachment[]> {
    return this.uploadStateComponent.associateUploadedFiles(recordId);
  }

  getAttachmentId(attachment: RecordAttachment): string {
    return this.attachmentService.getAttachmentId(attachment);
  }

  private onAttachmentsUpdated(attachments: RecordAttachment[]) {
    if (!ArrayUtils.containSameItems(this.lastProcessedAttachments, attachments)) {
      this.attachmentsWithPreview = [];
      this.attachmentsWithoutPreview = [];
      attachments.forEach(attachment => {
        if (attachment.preview) {
          this.attachmentsWithPreview.push(attachment);
        } else {
          this.attachmentsWithoutPreview.push(attachment);
        }
      });
      this.album = this.createLightboxAlbum(this.attachmentsWithPreview);
      if (this.toOpenAttachmentOnFetch) {
        this.openAttachmentById(this.attachmentIdToOpen);
        this.toOpenAttachmentOnFetch = false;
      }
      this.lastProcessedAttachments = attachments;
    }
  }

  private resetFileSelectors(): void {
    [this.selectDirective, this.dropDirective].forEach(fileSelectDirective => {
      if (fileSelectDirective) {
        fileSelectDirective.files = [];
      }
    });
  }

  /** As the links to attachments and to previews expire, need to ensure they are refreshed before that happens */
  private handleAttachmentClick(attachment: RecordAttachment, lightboxIndex: number, action: AttachmentClickAction): void {
    const threshold = this.attachmentService.getExpirationTs(attachment.link)?.subtract(this.linkExpiryThresholdMargin);
    if (threshold?.isBefore(moment())) {
      // Refresh attachments
      const fetchingAttachments = this.attachmentClickData?.attachmentId;
      this.attachmentClickData = {attachmentId: this.getAttachmentId(attachment), action};
      // If the attachments are already being fetched, no need to do it again
      if (!fetchingAttachments) {
        this.refetchAttachments().pipe(
          finalize(() => this.attachmentClickData = null),
          takeUntil(this.ngUnsubscribe),
        ).subscribe(attachments => {
          this.onAttachmentsUpdated(attachments);
          let attachmentToOpen: RecordAttachment = null;
          let attachmentToOpenIndex: number = null;
          if (this.attachmentClickData.action === 'download') {
            attachmentToOpen = attachments?.find(a => this.getAttachmentId(a) === this.attachmentClickData.attachmentId);
          } else if (this.attachmentClickData.action === 'lightbox') {
            attachmentToOpenIndex = this.attachmentsWithPreview
                && this.findIndexForPreviewAttachments(this.attachmentClickData.attachmentId);
            attachmentToOpen = this.attachmentsWithPreview[attachmentToOpenIndex];
          } else {
            throw new Error('Unknown attachment action "' + this.attachmentClickData.action + '"');
          }
          if (attachmentToOpen) {
            this.handleAttachmentAction(attachmentToOpen, attachmentToOpenIndex, this.attachmentClickData.action);
          }
        });
      }
    } else {
      this.handleAttachmentAction(attachment, lightboxIndex, action);
    }
  }

  private refetchAttachments(): Observable<RecordAttachment[]> {
    this.attachmentsFetching = true;
    return this.attachmentService.getAllAttachments(this.recordId).pipe(
      tap(attachments => this.attachmentsFetched.emit(attachments)),
      finalize(() => this.attachmentsFetching = false)
    );
  }

  private handleAttachmentAction(attachment: RecordAttachment, lightboxIndex: number, action: AttachmentClickAction): void {
    const isLightboxToOpen = action === 'lightbox';
    if (this.lightboxData && isLightboxToOpen) {
      this.ignoreLightboxCloseEvent = true;
    }
    this.closeLightbox();
    if (action === 'download') {
      saveAs(attachment.link.url.toString(), attachment.file_name);
    } else if (isLightboxToOpen) {
      const album = [...this.album];

      const subscription = this.lightboxEvent.lightboxEvent$.subscribe((event: LightboxEventObject) => {
        if (event.id === LIGHTBOX_EVENT.CLOSE) {
          this.clearLightboxData();
        } else if (event.id === LIGHTBOX_EVENT.CHANGE_PAGE) {
          const index: number = event.data;
          this.lightboxData.index = index;
          this.lightboxData.attachment_id = this.getAttachmentId(this.attachmentsWithPreview[index]);
        }
        this.emitAttachmentPreviewUserAction(event);
      });
      this.lightboxData = {
        subscription: subscription,
        timeoutHandle: this.scheduleLightboxRefresh(attachment),
        attachment_id: this.getAttachmentId(attachment),
        index: lightboxIndex,
        album: album,
      };
      this.lightbox.open(album, lightboxIndex);
    } else {
      throw new Error('Unknown attachment action "' + action + '"');
    }
    this.attachmentOpened.emit(attachment);
  }

  private getLightboxRefreshSleepDuration(attachment: RecordAttachment): number {
    const expirationTs = (attachment?.link && this.attachmentService.getExpirationTs(attachment.link)) ?? null;
    if (expirationTs) {
      return moment(expirationTs).subtract(2, 'minute').valueOf() - new Date().getTime();
    } else {
      return 0;
    }
  }

  private scheduleLightboxRefresh(attachment: RecordAttachment) {
    const sleepDuration = this.getLightboxRefreshSleepDuration(attachment);
    if (sleepDuration > 0) {
      return setTimeout(this.updateOpenedLightbox.bind(this), sleepDuration);
    } else {
      this.logger.error('Cannot schedule refresh for lightbox as sleep duration is "' + sleepDuration + '"');
      return null;
    }
  }

  private updateOpenedLightbox() {
    this.refetchAttachments().subscribe(() => {
      // If the lightbox was closed while the attachments were being fetched this.lightboxData will be null
      if (this.lightboxData && this.attachments && this.attachmentsWithPreview && this.attachmentsWithPreview.length > this.lightboxData.index) {
        if (this.lightboxData.attachment_id === this.getAttachmentId(this.attachmentsWithPreview[this.lightboxData.index]) &&
            // If the shown image is the last one then there will be no "Next" image shown. Otherwise, check that the new attachments
            // have enough items for the next image to be shown. If not, have to close and reopen the light box
            (this.lightboxData.index === this.lightboxData.album.length - 1 || this.attachmentsWithPreview.length > this.lightboxData.index + 1)) {
          // Update the album lightbox is using to reflect the latest URLs and attachments
          this.attachmentsWithPreview.forEach((a, index) => {
            if (index < this.lightboxData.album.length) {
              const iAlbum = this.lightboxData.album[index];
              iAlbum.src = a.link.url.toString();
              iAlbum.thumb = a.link.url.toString();
              iAlbum.caption = a.file_name;
            } else {
              this.lightboxData.album.push({ src: a.link.url.toString(), thumb: a.link.url.toString(), caption: a.file_name, downloadUrl: null });
            }
          });
          const lengthDiff = this.lightboxData.album.length - this.attachmentsWithPreview.length;
          if (lengthDiff > 0) {
            this.lightboxData.album.splice(this.attachmentsWithPreview.length, lengthDiff);
          }
          this.scheduleLightboxRefresh(this.attachmentsWithPreview[this.lightboxData.index]);
        } else {
          const index = this.findIndexForPreviewAttachments(this.lightboxData.attachment_id);
          if (index >= 0) {
            this.handleAttachmentAction(this.attachmentsWithPreview[index], index, 'lightbox');
          } else {
            this.closeLightbox();
          }
        }
      } else {
        this.closeLightbox();
      }
    });
  }

  private closeLightbox(): void {
    if (this.lightboxData) {
      this.lightbox.close();
      this.clearLightboxData();
    }
  }

  private clearLightboxData(): void {
    if (this.lightboxData != null) {
      if (this.lightboxData.subscription != null) {
        this.lightboxData.subscription.unsubscribe();
      }
      if (this.lightboxData.timeoutHandle != null) {
        clearTimeout(this.lightboxData.timeoutHandle);
      }
      this.lightboxData = null;
    }
  }

  private emitAttachmentPreviewUserAction(event: LightboxEventObject): void {
    const closeEvent = event.id === LIGHTBOX_EVENT.CLOSE;
    if (closeEvent && this.ignoreLightboxCloseEvent) {
      this.ignoreLightboxCloseEvent = false;
      return;
    }
    const previewActionEvent: AttachmentPreviewUserAction = {action: this.attachmentUserActionByLightboxEvent.get(event.id)};
    if (previewActionEvent.action != null) {
      if (!closeEvent) {
        const albumIndex = this.lightboxData.index;
        previewActionEvent.attachmentId = this.lightboxData.attachment_id;
        previewActionEvent.attachmentName = this.lightboxData.album[albumIndex]?.caption;
      }
      this.attachmentPreviewUserAction.emit(previewActionEvent);
    }
  }

  private findIndexForAttachments(attachments: RecordAttachment[], attachmentId: string): number {
    return attachments.findIndex(attachment => this.getAttachmentId(attachment) === attachmentId);
  }

  private findIndexForPreviewAttachments(attachmentId: string): number {
    return this.findIndexForAttachments(this.attachmentsWithPreview, attachmentId);
  }

  private createLightboxAlbum(attachmentsWithPreview: RecordAttachment[]): IAlbum[] {
    return attachmentsWithPreview?.map(a => ({
      src: a.link && a.link.url && a.link.url.toString(),
      thumb: a.link && a.link.url && a.link.url.toString(),
      caption: a.file_name,
      downloadUrl: null
    })) ?? [];
  }
}
